import { motion } from 'framer-motion';

const Service2 = () => {
  return (
    <div className="w-full min-h-screen bg-black py-20 px-6 lg:px-16 flex items-center justify-center overflow-hidden">
      {/* Animated floating blue cards */}
      <div className="absolute inset-0 pointer-events-none">
        <motion.div 
          className="absolute -top-40 -left-40 w-80 h-80 bg-gradient-to-r from-blue-800/20 to-blue-900/20 backdrop-blur-xl rounded-full border border-blue-900/30"
          animate={{ 
            scale: [1, 1.1, 1], 
            rotate: [0, 10, 0],
            y: [0, -20, 0]
          }}
          transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div 
          className="absolute top-40 right-20 w-64 h-64 bg-gradient-to-r from-blue-800/20 to-blue-900/20 backdrop-blur-xl rounded-full border border-blue-900/30"
          animate={{ 
            scale: [1, 1.15, 1], 
            rotate: [0, -8, 0],
            y: [0, 15, 0]
          }}
          transition={{ duration: 25, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>

      {/* Main card entrance animation */}
      <motion.div
        className="relative w-full max-w-5xl group"
        initial={{ opacity: 0, scale: 0.95, y: 60 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 1, ease: "easeOut" }}
      >
        <div className="relative bg-blue-900/80 backdrop-blur-2xl rounded-3xl p-2 lg:p-4 shadow-2xl border border-blue-900/40">
          <div className="bg-gradient-to-br from-blue-900 via-blue-800/90 to-blue-700/60 backdrop-blur-xl rounded-2xl p-8 lg:p-12 flex flex-col lg:flex-row items-center lg:items-stretch overflow-hidden border border-blue-900/30 shadow-xl">
            
            {/* Left: Kinetic typography + organic shapes */}
            <motion.div 
              className="w-full lg:w-1/2 h-96 lg:h-full flex flex-col justify-center lg:pr-16 pb-12 lg:pb-0 relative overflow-hidden"
              initial={{ x: -100 }}
              animate={{ x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              {/* Animated organic shapes */}
              <motion.div 
                className="absolute -top-20 -right-20 w-40 h-40 bg-gradient-to-br from-indigo-500/10 to-purple-500/10 rounded-3xl -rotate-12 blur-sm"
                animate={{ rotate: [0, 5, 0], scale: [1, 1.1, 1] }}
                transition={{ duration: 6, repeat: Infinity }}
              />
              
              <motion.h2 
                className="text-5xl lg:text-7xl xl:text-8xl font-black bg-gradient-to-r from-blue-200 via-blue-300 to-blue-500 bg-clip-text text-transparent leading-[0.9] mb-12 -skew-x-3 lg:-skew-x-1 tracking-[-2px] drop-shadow-2xl"
                initial={{ opacity: 0, scale: 0.8, y: 60 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                Our Services
              </motion.h2>

              <motion.p 
                className="text-xl lg:text-2xl text-blue-100 font-medium leading-relaxed mb-10 max-w-lg opacity-90"
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.6 }}
              >
                Transform your business with our cutting-edge solutions. 
                Experience innovation that drives results.
              </motion.p>

              {/* Micro-interaction button */}
              <motion.button
                className="group/btn relative bg-gradient-to-r from-blue-800 to-blue-900 hover:from-blue-900 hover:to-blue-950 text-white px-10 py-6 rounded-3xl font-bold text-lg shadow-2xl hover:shadow-3xl border border-blue-900/50 overflow-hidden transition-all duration-300 flex items-center gap-4 uppercase tracking-wider"
                initial={{ scale: 0.9, opacity: 0, y: 40 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                whileHover={{ 
                  scale: 1.05, 
                  boxShadow: "0 25px 50px rgba(99, 102, 241, 0.4)",
                  y: -4
                }}
                whileTap={{ scale: 0.95 }}
                transition={{ duration: 0.4, delay: 0.8 }}
              >
                <span>Discover More</span>
                <motion.div
                  className="w-8 h-8 border-r-4 border-t-4 border-white rotate-45"
                  initial={{ x: 0 }}
                  whileHover={{ x: 8 }}
                  transition={{ type: "spring", stiffness: 400 }}
                />
                <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-white/20 -translate-x-full group-hover/btn:translate-x-full transition-transform duration-1000" />
              </motion.button>
            </motion.div>

            {/* Right: 3D layered workspace - modern 2026 aesthetic */}
            <motion.div 
              className="w-full lg:w-1/2 h-96 lg:h-full relative mt-12 lg:mt-0"
              initial={{ x: 100 }}
              animate={{ x: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              {/* Floating 3D monitor */}
              <motion.div
                className="absolute top-16 right-12 w-64 h-48 bg-gradient-to-br from-blue-900 to-blue-800 rounded-2xl shadow-2xl border border-blue-900/70 overflow-hidden hover:scale-105 transition-all duration-300 -rotate-2"
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
                whileHover={{ rotate: 0, y: -10 }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-blue-900/20 to-blue-800/20" />
                <div className="absolute inset-2 bg-blue-950 rounded-xl shadow-inner" />
                <div className="absolute top-4 left-4 w-3 h-3 bg-blue-900 rounded-full shadow-lg" />
              </motion.div>

              {/* Floating devices */}
              <motion.div
                className="absolute top-8 left-16 w-20 h-32 bg-gradient-to-b from-blue-950 to-blue-900 rounded-2xl shadow-2xl border border-blue-900/30 rotate-6 hover:rotate-12 transition-all duration-300"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.7, delay: 0.8 }}
                whileHover={{ y: -15 }}
              />
              
              <motion.div
                className="absolute bottom-20 right-8 w-16 h-24 bg-gradient-to-b from-blue-800 to-blue-900 rounded-2xl shadow-2xl border border-blue-900/40 -rotate-3 hover:rotate-1 transition-all duration-300"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.7, delay: 0.9 }}
                whileHover={{ y: -20, scale: 1.1 }}
              />

              {/* Animated connection lines */}
              <motion.div
                className="absolute top-1/2 left-1/2 w-64 h-0.5 bg-gradient-to-r from-blue-900/50 to-blue-800/50 rounded-full blur-sm"
                animate={{ 
                  scaleX: [0, 1, 0.8, 1], 
                  rotate: [0, 5, -3, 0]
                }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              />

              {/* Service cards floating */}
              <motion.div
                className="absolute bottom-12 left-12 w-32 h-20 bg-gradient-to-br from-blue-900/80 to-blue-800/80 backdrop-blur-xl rounded-2xl shadow-xl border border-blue-900/70 flex items-center justify-center text-sm font-semibold text-blue-100 rotate-1 hover:rotate-0 transition-all duration-300"
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.7, delay: 1 }}
                whileHover={{ y: -10, scale: 1.05 }}
              >
                Web Design
              </motion.div>

              <motion.div
                className="absolute bottom-8 right-16 w-28 h-16 bg-gradient-to-br from-blue-900/80 to-blue-800/80 backdrop-blur-xl rounded-2xl shadow-xl border border-blue-900/70 flex items-center justify-center text-sm font-semibold text-blue-100 -rotate-2 hover:rotate-0 transition-all duration-300"
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.7, delay: 1.1 }}
                whileHover={{ y: -10, scale: 1.05 }}
              >
                Development
              </motion.div>
            </motion.div>
          </div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          className="absolute -bottom-12 left-1/2 transform -translate-x-1/2 text-center text-sm text-blue-900"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.2 }}
        >
          <div className="w-px h-12 bg-gradient-to-b from-blue-900 to-transparent mx-auto mb-2 animate-pulse" />
          Scroll to explore
        </motion.div>
      </motion.div>
    </div>
  );
};

export default Service2;
